import { Plus, Trash2 } from "lucide-react";
import { InputField, SelectField } from "./DesignRequestFormV3";
import { configuredOptions } from "./IntakeFormOptionUtils";

const MAX_ADDITIONAL_CONTACTS = 3;
const blankAdditionalContact = () => ({
  name: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip: "",
});

export const DesignRequestProjectSection = ({
  form,
  update,
  options,
  manufacturerOptions,
  handleManufacturerChange,
  isrNames,
  osrNames,
  handleBeverageTypeChange,
  handlePrintTypeChange,
  artworkPoRequired = false,
}) => {
  const isDigitalPrint = String(form.print_type || form.application_type || "").toLowerCase().includes("digital");
  const additionalContacts = Array.isArray(form.additional_contacts) ? form.additional_contacts : [];

  const updateAdditionalContact = (index, field, value) => {
    const next = additionalContacts.map((contact, contactIndex) => (
      contactIndex === index ? { ...contact, [field]: value } : contact
    ));
    update("additional_contacts", next);
  };

  const addAdditionalContact = () => {
    if (additionalContacts.length >= MAX_ADDITIONAL_CONTACTS) return;
    update("additional_contacts", [...additionalContacts, blankAdditionalContact()]);
  };

  const removeAdditionalContact = (index) => {
    update("additional_contacts", additionalContacts.filter((_, contactIndex) => contactIndex !== index));
  };

  return (
    <>
      <div className="dof-section-title">CUSTOMER CONTACT</div>
      <div className="dof-two-col">
        <div className="dof-col">
          <InputField label="Customer" value={form.company_name} onChange={(value) => update("company_name", value)} required />
          <InputField label="Contact Name" value={form.contact_name} onChange={(value) => update("contact_name", value)} required />
          <InputField label="Contact Email" type="email" value={form.contact_email} onChange={(value) => update("contact_email", value)} required />
          <InputField label="Contact Phone" value={form.contact_phone} onChange={(value) => update("contact_phone", value)} />
        </div>
        <div className="dof-col">
          <InputField label="Street Address" value={form.contact_street || ""} onChange={(value) => update("contact_street", value)} />
          <InputField label="City" value={form.contact_city || ""} onChange={(value) => update("contact_city", value)} />
          <InputField label="State" value={form.contact_state || ""} onChange={(value) => update("contact_state", value)} />
          <InputField label="ZIP" value={form.contact_zip || ""} onChange={(value) => update("contact_zip", value)} />
        </div>
      </div>

      <div className="dof-contact-sub" style={{ marginTop: "1rem" }}>
        <div className="dof-sku-heading-row">
          <h4 className="dof-contact-heading">Additional Contacts</h4>
          <button
            type="button"
            onClick={addAdditionalContact}
            disabled={additionalContacts.length >= MAX_ADDITIONAL_CONTACTS}
            className="dof-back-btn"
            style={{ opacity: additionalContacts.length >= MAX_ADDITIONAL_CONTACTS ? 0.5 : 1 }}
          >
            <Plus size={14} /> Add Another Contact
          </button>
        </div>
        <p className="dof-note-text" style={{ marginBottom: "0.75rem" }}>
          Add up to three additional contacts when a customer has multiple people involved in artwork, samples, or approvals.
        </p>
        {additionalContacts.length === 0 ? (
          <p className="dof-note-text">No additional contacts added.</p>
        ) : additionalContacts.map((contact, index) => (
          <div key={index} className="dof-conditional-panel" style={{ marginTop: "0.75rem" }}>
            <div className="dof-sku-heading-row">
              <div className="dof-conditional-title">Contact {index + 2}</div>
              <button type="button" onClick={() => removeAdditionalContact(index)} className="dof-back-btn">
                <Trash2 size={14} /> Remove
              </button>
            </div>
            <div className="dof-prod-grid">
              <InputField label="Contact Name" value={contact.name || ""} onChange={(value) => updateAdditionalContact(index, "name", value)} />
              <InputField label="Contact Email" type="email" value={contact.email || ""} onChange={(value) => updateAdditionalContact(index, "email", value)} />
              <InputField label="Contact Phone" value={contact.phone || ""} onChange={(value) => updateAdditionalContact(index, "phone", value)} />
              <InputField label="Street Address" value={contact.street || ""} onChange={(value) => updateAdditionalContact(index, "street", value)} />
              <InputField label="City" value={contact.city || ""} onChange={(value) => updateAdditionalContact(index, "city", value)} />
              <InputField label="State" value={contact.state || ""} onChange={(value) => updateAdditionalContact(index, "state", value)} />
              <InputField label="ZIP" value={contact.zip || ""} onChange={(value) => updateAdditionalContact(index, "zip", value)} />
            </div>
          </div>
        ))}
      </div>

      <div className="dof-section-title">PROJECT INFORMATION</div>
      <div className="dof-two-col">
        <div className="dof-col">
          <SelectField label="Manufacturer" value={form.manufacturer_id} onChange={handleManufacturerChange} required options={manufacturerOptions} />
          <SelectField label="Priority" value={form.priority} onChange={(value) => update("priority", value)} options={configuredOptions(options, "priority", form.priority)} />
          <SelectField label="Sales Ops" value={form.isr} onChange={(value) => update("isr", value)} required options={isrNames.map((name) => ({ value: name, label: name }))} />
          <SelectField label="Outside Sales Rep (OSR)" value={form.osr} onChange={(value) => update("osr", value)} required options={osrNames.map((name) => ({ value: name, label: name }))} />
          <InputField label="Filler" value={form.filler} onChange={(value) => update("filler", value)} />
          <InputField label="Brand" value={form.brand} onChange={(value) => update("brand", value)} />
        </div>

        <div className="dof-col">
          <div className="isr-only-field">
            <InputField label="Artwork PO" value={form.gamer_po} onChange={(value) => update("gamer_po", value)} required={artworkPoRequired} />
          </div>
          <InputField label="Customer PO" value={form.customer_po} onChange={(value) => update("customer_po", value)} />
          <InputField label="Need By Date" type="date" value={form.deadline} onChange={(value) => update("deadline", value)} />
          <SelectField label="Print Type" value={form.print_type || form.application_type} onChange={handlePrintTypeChange || ((value) => update("print_type", value))} options={configuredOptions(options, "print_type", form.print_type || form.application_type)} />
          {isDigitalPrint && (
            <SelectField label="Decoration Method" value={form.decoration_method} onChange={(value) => update("decoration_method", value)} options={configuredOptions(options, "decoration_method", form.decoration_method)} />
          )}
          <SelectField label="Beverage Type" value={form.beverage_type} onChange={handleBeverageTypeChange} options={configuredOptions(options, "beverage_type", form.beverage_type)} />
          {form.beverage_type === "Beer" && (
            <SelectField label="Beer Type" value={form.beer_type} onChange={(value) => update("beer_type", value)} options={configuredOptions(options, "beer_type", form.beer_type)} />
          )}
          <SelectField label="Can Size" value={form.can_size} onChange={(value) => update("can_size", value)} required options={configuredOptions(options, "can_size", form.can_size)} />
          {form.can_size === "Other" && <InputField label="Other Can Size" value={form.can_size_other} onChange={(value) => update("can_size_other", value)} required />}
          <SelectField label="End Size" value={form.can_finish} onChange={(value) => update("can_finish", value)} required options={configuredOptions(options, "end_size", form.can_finish)} />
          <SelectField label="End Type" value={form.lid_type} onChange={(value) => update("lid_type", value)} options={configuredOptions(options, "end_type", form.lid_type)} />
        </div>
      </div>
    </>
  );
};
